import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Shield, ShieldCheck, Smartphone, Monitor, LogOut, Key, Loader2, Copy, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';
import { toast } from 'sonner';

export default function SecurityTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Fetch sessions
  const { data: sessionsData } = useQuery({
    queryKey: ['auth-sessions'],
    queryFn: () => api.get('/auth/sessions'),
  });
  
  // Fetch login history
  const { data: historyData } = useQuery({
    queryKey: ['login-history'],
    queryFn: () => api.get('/auth/login-history'),
  });
  
  // Check current 2FA status from user profile
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get('/profiles/me'),
  });
  
  const is2FAEnabled = profile?.two_factor_enabled;
  
  // 2FA Setup
  const handleSetup2FA = async () => {
    try {
      const data = await api.post('/auth/2fa/setup');
      setSetupData(data);
      setBackupCodes(data.backup_codes || []);
      setSetupOpen(true);
    } catch (e) {
      toast.error(e.message || 'Failed to start 2FA setup');
    }
  };
  
  // Verify 2FA setup
  const handleVerifySetup = async () => {
    try {
      await api.post('/auth/2fa/verify-setup', { code: verifyCode });
      toast.success(t('security.2faEnabled') || '2FA enabled successfully!');
      setSetupOpen(false);
      setVerifyCode('');
      setBackupCodesOpen(true);
      queryClient.invalidateQueries(['profile']);
    } catch (e) {
      toast.error(e.message || 'Invalid code');
    }
  };
  
  // Disable 2FA
  const handleDisable2FA = async () => {
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      toast.success(t('security.2faDisabled') || '2FA disabled');
      setDisableOpen(false);
      setDisablePassword('');
      queryClient.invalidateQueries(['profile']);
    } catch (e) {
      toast.error(e.message || 'Invalid password');
    }
  };
  
  // Revoke session
  const handleRevokeSession = async (sessionId) => {
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      toast.success(t('security.sessionRevoked') || 'Session revoked');
      queryClient.invalidateQueries(['auth-sessions']);
    } catch (e) {
      toast.error('Failed to revoke session');
    }
  };
  
  // Logout everywhere
  const handleLogoutAll = async () => {
    try {
      await api.delete('/auth/sessions');
      toast.success(t('security.allSessionsRevoked') || 'All sessions revoked');
      queryClient.invalidateQueries(['auth-sessions']);
    } catch (e) {
      toast.error('Failed');
    }
  };
  
  const sessions = sessionsData?.sessions || [];
  const history = historyData?.history || [];
  
  return (
    <div className="space-y-6">
      {/* 2FA Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            {t('security.twoFactor') || 'Two-Factor Authentication'}
          </CardTitle>
          <CardDescription>
            {t('security.twoFactorDesc') || 'Add an extra layer of security to your account with TOTP-based verification.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant={is2FAEnabled ? "default" : "secondary"}>
                {is2FAEnabled ? (t('security.enabled') || 'Enabled') : (t('security.disabled') || 'Disabled')}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {is2FAEnabled
                  ? (t('security.2faActiveDesc') || 'Your account is protected with two-factor authentication')
                  : (t('security.2faInactiveDesc') || 'Enable 2FA to protect your account')}
              </span>
            </div>
            {is2FAEnabled ? (
              <Button variant="destructive" size="sm" onClick={() => setDisableOpen(true)}>
                {t('security.disable') || 'Disable'}
              </Button>
            ) : (
              <Button size="sm" onClick={handleSetup2FA}>
                <Smartphone className="h-4 w-4 mr-1.5" />
                {t('security.enable2FA') || 'Enable 2FA'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                {t('security.activeSessions') || 'Active Sessions'}
              </CardTitle>
              <CardDescription>
                {t('security.activeSessionsDesc') || 'Devices currently logged into your account.'}
              </CardDescription>
            </div>
            {sessions.length > 1 && (
              <Button variant="outline" size="sm" onClick={handleLogoutAll} className="text-red-600">
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                {t('security.logoutAll') || 'Log out everywhere'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {sessions.map((session, i) => (
              <div key={session.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <Monitor className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{session.device}</p>
                    <p className="text-xs text-muted-foreground">
                      {session.ip_address} · {t('security.lastUsed') || 'Last used'}: {session.last_used ? new Date(session.last_used).toLocaleString() : '—'}
                    </p>
                  </div>
                  {i === 0 && <Badge variant="outline" className="text-xs">{t('security.currentSession') || 'Current'}</Badge>}
                </div>
                {i > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => handleRevokeSession(session.id)}>
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
            {sessions.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">{t('security.noSessions') || 'No active sessions'}</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Login History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            {t('security.loginHistory') || 'Login History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {history.slice(0, 10).map((entry, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-2">
                  {entry.action === 'auth.login' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-sm">
                    {entry.action === 'auth.login' ? (t('security.successfulLogin') || 'Successful login') : (t('security.failedLogin') || 'Failed login attempt')}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  <div>{entry.ip_address}</div>
                  <div>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}</div>
                </div>
              </div>
            ))}
            {history.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">{t('security.noHistory') || 'No login history'}</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* 2FA Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('security.setup2FA') || 'Set Up Two-Factor Authentication'}</DialogTitle>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('security.scanQR') || 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), or enter the secret key manually.'}
                </p>
                <div className="flex justify-center p-4 bg-white rounded-lg border">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauth_url)}`} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              </div>
              <div>
                <Label>{t('security.secretKey') || 'Secret Key (manual entry)'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={setupData.secret} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success('Copied!'); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('security.enterCode') || 'Enter the 6-digit code from your app'}</Label>
                <Input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  onKeyDown={(e) => e.key === 'Enter' && verifyCode.length === 6 && handleVerifySetup()}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)}>{t('security.cancel') || 'Cancel'}</Button>
            <Button onClick={handleVerifySetup} disabled={verifyCode.length !== 6}>{t('security.verify') || 'Verify & Enable'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Backup Codes Dialog */}
      <Dialog open={backupCodesOpen} onOpenChange={setBackupCodesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('security.saveBackupCodes') || 'Save Your Backup Codes'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('security.backupCodesDesc') || 'Save these codes somewhere safe. Each code can only be used once. If you lose your authenticator app, you can use these to sign in.'}
          </p>
          <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
            {backupCodes.map((code, i) => (
              <div key={i} className="text-center py-1">{code}</div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success('Copied!'); }}>
              <Copy className="h-3.5 w-3.5 mr-1.5" /> {t('security.copyAll') || 'Copy All'}
            </Button>
            <Button onClick={() => setBackupCodesOpen(false)}>{t('security.done') || 'Done'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Disable 2FA Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('security.disable2FA') || 'Disable Two-Factor Authentication'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('security.disable2FADesc') || 'Enter your password to confirm. This will remove 2FA protection from your account.'}
          </p>
          <div>
            <Label>{t('security.password') || 'Password'}</Label>
            <Input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className="mt-1"
              onKeyDown={(e) => e.key === 'Enter' && handleDisable2FA()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>{t('security.cancel') || 'Cancel'}</Button>
            <Button variant="destructive" onClick={handleDisable2FA} disabled={!disablePassword}>{t('security.confirmDisable') || 'Disable 2FA'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
